import { useState } from 'react';
import { formatDate } from '../utils/timezone';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useWorkOrder,
  useUpdateWorkOrder,
  useDeleteWorkOrder,
  useSubmitWorkOrder,
  useConfirmWorkOrder,
  useStartWorkOrder,
  useCompleteWorkOrder,
  useCancelWorkOrder,
  useWorkOrderItems,
  useAddWorkOrderItem,
  useRemoveWorkOrderItem,
  useWorkOrderSerials,
  useAssignSerial,
  useUnassignSerial,
} from '../hooks/useWmsWorkOrders';
import { useSerializedItems } from '../hooks/useWmsInventory';
import { usePickingLists } from '../hooks/useWmsPicking';
import { usePackingSlips } from '../hooks/useWmsShipping';
import { WorkOrderStatusBadge, WorkOrderPriorityBadge } from '../components/work-orders/WorkOrderStatusBadge';
import { WorkOrderFormModal } from '../components/work-orders/WorkOrderFormModal';
import { WorkOrderItemsTable } from '../components/work-orders/WorkOrderItemsTable';
import { WorkOrderSerialsTable } from '../components/work-orders/WorkOrderSerialsTable';
import { AddItemModal } from '../components/work-orders/AddItemModal';
import { AssignSerialModal } from '../components/work-orders/AssignSerialModal';
import { PickingListStatusBadge } from '../components/picking/PickingStatusBadge';
import { PackingSlipStatusBadge } from '../components/shipping/PackingSlipStatusBadge';
import { ConfirmDialog, PromptDialog } from '../components/shared/ConfirmDialog';
import type {
  WorkOrder,
  WorkOrderItem,
  WorkOrderSerial,
  UpdateWorkOrderInput,
  AddWorkOrderItemInput,
  AssignSerialInput,
  SerializedItemsResponse,
  PickingListsResponse,
  PackingSlipsResponse,
} from '../types';

export default function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [assignSerialItem, setAssignSerialItem] = useState<WorkOrderItem | null>(null);

  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<WorkOrderItem | null>(null);
  const [serialToUnassign, setSerialToUnassign] = useState<WorkOrderSerial | null>(null);

  // Queries
  const workOrderQuery = useWorkOrder(id || '');
  const itemsQuery = useWorkOrderItems(id || '');
  const serialsQuery = useWorkOrderSerials(id || '');
  const serializedItemsQuery = useSerializedItems({ item_status: 'in_stock', page_length: 100 });
  const pickingListsQuery = usePickingLists({ work_order_id: id });
  const packingSlipsQuery = usePackingSlips({ work_order_id: id });

  // Mutations
  const updateMutation = useUpdateWorkOrder();
  const deleteMutation = useDeleteWorkOrder();
  const submitMutation = useSubmitWorkOrder();
  const confirmMutation = useConfirmWorkOrder();
  const startMutation = useStartWorkOrder();
  const completeMutation = useCompleteWorkOrder();
  const cancelMutation = useCancelWorkOrder();
  const addItemMutation = useAddWorkOrderItem();
  const removeItemMutation = useRemoveWorkOrderItem();
  const assignSerialMutation = useAssignSerial();
  const unassignSerialMutation = useUnassignSerial();

  const workOrder = workOrderQuery.data as WorkOrder | undefined;
  const items = (itemsQuery.data as WorkOrderItem[] | undefined) || [];
  const serials = (serialsQuery.data as WorkOrderSerial[] | undefined) || [];
  const serializedItemsData = serializedItemsQuery.data as SerializedItemsResponse | undefined;
  const availableSerials = serializedItemsData?.items || [];
  const pickingListsData = pickingListsQuery.data as PickingListsResponse | undefined;
  const pickingLists = pickingListsData?.items || [];
  const packingSlipsData = packingSlipsQuery.data as PackingSlipsResponse | undefined;
  const packingSlips = packingSlipsData?.items || [];

  const handleUpdate = async (data: UpdateWorkOrderInput) => {
    if (!id) return;
    await updateMutation.mutateAsync({ id, data });
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteMutation.mutateAsync(id);
    navigate('/wms/work-orders');
  };

  const handleSubmit = async () => {
    if (!id) return;
    await submitMutation.mutateAsync(id);
  };

  const handleConfirm = async () => {
    if (!id) return;
    await confirmMutation.mutateAsync(id);
  };

  const handleStart = async () => {
    if (!id) return;
    await startMutation.mutateAsync(id);
  };

  const handleComplete = async () => {
    if (!id) return;
    await completeMutation.mutateAsync(id);
  };

  const handleCancel = async (reason?: string) => {
    if (!id) return;
    await cancelMutation.mutateAsync({ id, reason: reason || undefined });
  };

  const handleAddItem = async (data: AddWorkOrderItemInput) => {
    if (!id) return;
    await addItemMutation.mutateAsync({ workOrderId: id, data });
  };

  const handleRemoveItem = async () => {
    if (!id || !itemToRemove) return;
    await removeItemMutation.mutateAsync({ workOrderId: id, itemId: itemToRemove.id });
    setItemToRemove(null);
  };

  const handleAssignSerial = async (data: AssignSerialInput) => {
    if (!id) return;
    await assignSerialMutation.mutateAsync({ workOrderId: id, data });
  };

  const handleUnassignSerial = async () => {
    if (!id || !serialToUnassign) return;
    await unassignSerialMutation.mutateAsync({ workOrderId: id, allocationId: serialToUnassign.id });
    setSerialToUnassign(null);
  };

  // formatDate is imported from utils/timezone (Montreal timezone)

  if (workOrderQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (workOrderQuery.error || !workOrder) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Work Order not found</h2>
        <p className="text-muted-foreground mt-2">
          The work order you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/wms/work-orders">Back to Work Orders</Link>
        </Button>
      </div>
    );
  }

  const canAddItems = workOrder.status === 'draft' || workOrder.status === 'pending';
  const canAllocate = workOrder.status === 'pending' || workOrder.status === 'confirmed' || workOrder.status === 'in_progress';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/wms/work-orders"
              className="text-muted-foreground hover:text-foreground"
            >
              Work Orders
            </Link>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-2xl font-bold tracking-tight font-mono">
              {workOrder.work_order_number}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">{workOrder.title}</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {workOrder.status === 'draft' && (
            <>
              <Button variant="outline" onClick={() => setShowEditModal(true)}>
                Edit
              </Button>
              <Button variant="outline" className="text-red-600" onClick={() => setShowDeleteConfirm(true)}>
                Delete
              </Button>
              <Button onClick={handleSubmit}>Submit</Button>
            </>
          )}
          {workOrder.status === 'pending' && (
            <>
              <Button variant="outline" onClick={() => setShowEditModal(true)}>
                Edit
              </Button>
              <Button variant="outline" className="text-red-600" onClick={() => setShowCancelPrompt(true)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={confirmMutation.isPending}>
                {confirmMutation.isPending ? 'Confirming...' : 'Confirm & Reserve'}
              </Button>
            </>
          )}
          {workOrder.status === 'confirmed' && (
            <>
              <Button variant="outline" className="text-red-600" onClick={() => setShowCancelPrompt(true)}>
                Cancel
              </Button>
              <Button onClick={handleStart} disabled={startMutation.isPending}>
                {startMutation.isPending ? 'Starting...' : 'Start Work'}
              </Button>
            </>
          )}
          {workOrder.status === 'in_progress' && (
            <>
              <Button variant="outline" className="text-red-600" onClick={() => setShowCancelPrompt(true)}>
                Cancel
              </Button>
              <Button onClick={handleComplete}>Complete</Button>
            </>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <WorkOrderStatusBadge status={workOrder.status} />
              <WorkOrderPriorityBadge priority={workOrder.priority} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{workOrder.account_name || '—'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Due:</span>
              <span>{formatDate(workOrder.due_date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span>{formatDate(workOrder.created_at)}</span>
            </div>
            {workOrder.started_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span>{formatDate(workOrder.started_at)}</span>
              </div>
            )}
            {workOrder.completed_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Completed:</span>
                <span>{formatDate(workOrder.completed_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {workOrder.total_allocated || 0} / {workOrder.total_ordered || 0}
            </div>
            <p className="text-sm text-muted-foreground">Items allocated</p>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      {(workOrder.description || workOrder.notes || workOrder.assigned_to_user_name) && (
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {workOrder.assigned_to_user_name && (
                <div>
                  <span className="text-sm text-muted-foreground">Assigned To</span>
                  <p className="font-medium">{workOrder.assigned_to_user_name}</p>
                </div>
              )}
              {workOrder.created_by_user_name && (
                <div>
                  <span className="text-sm text-muted-foreground">Created By</span>
                  <p className="font-medium">{workOrder.created_by_user_name}</p>
                </div>
              )}
              {workOrder.description && (
                <div className="col-span-2">
                  <span className="text-sm text-muted-foreground">Description</span>
                  <p className="font-medium">{workOrder.description}</p>
                </div>
              )}
              {workOrder.notes && (
                <div className="col-span-2">
                  <span className="text-sm text-muted-foreground">Notes</span>
                  <p className="font-medium">{workOrder.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Line Items</CardTitle>
          {canAddItems && (
            <Button size="sm" onClick={() => setShowAddItemModal(true)}>
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {itemsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <WorkOrderItemsTable
              items={items}
              onAssignSerial={canAllocate ? setAssignSerialItem : undefined}
              onRemoveItem={canAddItems ? (item) => setItemToRemove(item) : undefined}
              readOnly={!canAllocate && !canAddItems}
            />
          )}
        </CardContent>
      </Card>

      {/* Assigned Serials */}
      <Card>
        <CardHeader>
          <CardTitle>Assigned Serial Numbers</CardTitle>
        </CardHeader>
        <CardContent>
          {serialsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <WorkOrderSerialsTable
              serials={serials}
              onUnassign={canAllocate ? (serial) => setSerialToUnassign(serial) : undefined}
              readOnly={!canAllocate}
            />
          )}
        </CardContent>
      </Card>

      {/* Related Picking Lists */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Picking Lists</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/wms/picking?work_order_id=${id}`}>View All</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {pickingListsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pickingLists.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No picking lists created yet
            </p>
          ) : (
            <div className="space-y-2">
              {pickingLists.map((pl) => (
                <Link
                  key={pl.id}
                  to={`/wms/picking/${pl.id}`}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-medium">{pl.picking_number}</span>
                    <PickingListStatusBadge status={pl.status} />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {pl.items_picked ?? 0} / {pl.item_count ?? 0} items
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Related Packing Slips */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Packing Slips</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/wms/shipping?work_order_id=${id}`}>View All</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {packingSlipsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : packingSlips.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No packing slips created yet
            </p>
          ) : (
            <div className="space-y-2">
              {packingSlips.map((slip) => (
                <Link
                  key={slip.id}
                  to={`/wms/shipping/${slip.id}`}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-medium">{slip.slip_number}</span>
                    <PackingSlipStatusBadge status={slip.status} />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {slip.carrier && <span className="mr-2">{slip.carrier}</span>}
                    {slip.tracking_number && (
                      <span className="font-mono">{slip.tracking_number}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <WorkOrderFormModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdate}
        workOrder={workOrder}
        isLoading={updateMutation.isPending}
      />

      <AddItemModal
        open={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onSubmit={handleAddItem}
        isLoading={addItemMutation.isPending}
      />

      <AssignSerialModal
        open={!!assignSerialItem}
        onClose={() => setAssignSerialItem(null)}
        onSubmit={handleAssignSerial}
        workOrderItem={assignSerialItem}
        availableSerials={availableSerials}
        isLoading={assignSerialMutation.isPending}
      />

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Work Order"
        description={`Are you sure you want to delete work order "${workOrder?.work_order_number}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />

      <PromptDialog
        open={showCancelPrompt}
        onOpenChange={setShowCancelPrompt}
        title="Cancel Work Order"
        description={`Are you sure you want to cancel work order "${workOrder?.work_order_number}"?`}
        label="Cancellation reason"
        placeholder="Enter reason (optional)"
        confirmLabel="Cancel Work Order"
        onConfirm={handleCancel}
        isLoading={cancelMutation.isPending}
      />

      <ConfirmDialog
        open={!!itemToRemove}
        onOpenChange={(open) => !open && setItemToRemove(null)}
        title="Remove Item"
        description={`Are you sure you want to remove "${itemToRemove?.sku_code}" from this work order?`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleRemoveItem}
        isLoading={removeItemMutation.isPending}
      />

      <ConfirmDialog
        open={!!serialToUnassign}
        onOpenChange={(open) => !open && setSerialToUnassign(null)}
        title="Unassign Serial"
        description={`Are you sure you want to unassign serial number "${serialToUnassign?.serial_number}"?`}
        confirmLabel="Unassign"
        variant="destructive"
        onConfirm={handleUnassignSerial}
        isLoading={unassignSerialMutation.isPending}
      />
    </div>
  );
}
